import React from 'react';
import { useTranslation } from 'react-i18next';
import { AuthWrapper, AuthFormHeader } from '@src/features/auth/components';
import { useVerifyTotpForm } from '@src/features/auth/hooks';
import { REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { ArcButton, ArcOutlinedInput, ArcStack, ArcText } from '@src/core/ui';

const VerifyTotpPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    onSubmit,
    isLoading,
    errorMessage,
    lockSeconds,
    hasToken,
    useBackupCode,
    toggleBackupCode,
  } = useVerifyTotpForm();

  if (!hasToken) {
    return null;
  }

  const isLocked = lockSeconds > 0;

  return (
    <AuthWrapper>
      <AuthFormHeader
        title={t('mfa.login.title')}
        subtitle={
          useBackupCode
            ? t('mfa.login.subtitleBackup')
            : t('mfa.login.subtitleApp')
        }
      />
      <ArcStack
        mt={4}
        spacing={3}
        component='form'
        alignItems='center'
        onSubmit={onSubmit}
      >
        <ArcOutlinedInput
          fullWidth
          autoFocus
          autoComplete='one-time-code'
          label={useBackupCode ? t('mfa.login.backupCodeLabel') : t('mfa.login.codeLabel')}
          disabled={isLocked}
          error={Boolean(errors.code) || Boolean(errorMessage)}
          helperText={errors.code?.message || errorMessage || ''}
          {...register('code', { required: REQUIRED_VALIDATION_TEXT })}
        />

        {isLocked && (
          <ArcText fontSize={14} color='error.main' textAlign='center'>
            {t('mfa.login.locked', { seconds: lockSeconds })}
          </ArcText>
        )}

        <ArcButton.brand
          type='submit'
          sx={{ width: 300 }}
          isLoading={isLoading}
          disabled={isLocked}
        >
          {t('mfa.login.submit')}
        </ArcButton.brand>

        <ArcButton.tertiary type='button' onClick={toggleBackupCode} disabled={isLocked}>
          {useBackupCode
            ? t('mfa.login.useApp')
            : t('mfa.login.useBackup')}
        </ArcButton.tertiary>
      </ArcStack>
    </AuthWrapper>
  );
};

export default VerifyTotpPage;
